import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled, { keyframes } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  COLOR_PRIMARY,
  COLOR_LIGHT_RED,
  COLOR_RED,
} from "../../constants/color";
import { ArrowIcon } from "./icons/ArrowIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";
import { WhiteCloseIcon } from "./icons/WhiteCloseIcon";
import { MultiSelectDropdown } from "./MultiSelectDropdown";


export type MultiSelectOptionType = {
  id: number | string;
  name: string;
  value: any;
};

// Sortable tag component for drag-and-drop
type SortableTagProps = {
  option: MultiSelectOptionType;
  onRemove: (value: any) => void;
};

const SortableTag: React.FC<SortableTagProps> = ({ option, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(option.value) });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <StyledTag ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <StyledTagText>{option.name}</StyledTagText>
      <StyledRemoveButton
        onClick={(e) => {
          e.stopPropagation();
          onRemove(option.value);
        }}
        onPointerDown={(e) => e.stopPropagation()} // Prevent drag when clicking remove
      >
        <WhiteCloseIcon />
      </StyledRemoveButton>
    </StyledTag>
  );
};

type Props = {

  name: string;
  options: MultiSelectOptionType[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: string[];
  disabled?: boolean;
  onChange: (values: any[]) => void;
  clearable?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  width?: string;
  hasSearchInput?: boolean;
  selectedValues?: any[];
};

export const MultiSelect: React.FC<Props> = ({
  name,
  label,
  placeholder = "--",
  required,
  defaultValue = [],
  options,
  disabled,
  onChange,
  clearable = true,
  hasError,
  errorMessage,
  width = "100%",
  hasSearchInput = true,
  selectedValues = [],
}) => {
  const uuid = useMemo(() => {
    return uuidv4();
  }, []);

  const [isOpen, setIsOpen] = useState<boolean | null>(null);

  const formContext = useFormContext();

  // DnD Kit sensors for pointer and keyboard
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleOpenSelection = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleSelectOption = useCallback(
    (option: MultiSelectOptionType) => {
      // Check if option is already selected (with type coercion)
      const isSelected = selectedValues.some(value =>
        value === option.value || String(value) === String(option.value)
      );

      const newSelectedValues = isSelected
        ? selectedValues.filter(value =>
          value !== option.value && String(value) !== String(option.value)
        )
        : [...selectedValues, option.value];

      onChange(newSelectedValues);
      try {
        if (name) formContext.setValue(name, newSelectedValues, { shouldValidate: true });
      } catch (err) { }

      // Keep dropdown open for multi-select behavior
      // Don't close the dropdown when selecting/deselecting options
    },
    [selectedValues, onChange, name, formContext],
  );

  const handleRemoveValue = useCallback(
    (valueToRemove: any) => {
      const newSelectedValues = selectedValues.filter(value =>
        value !== valueToRemove && String(value) !== String(valueToRemove)
      );
      onChange(newSelectedValues);
      try {
        if (name) formContext.setValue(name, newSelectedValues, { shouldValidate: true });
      } catch (err) { }
    },
    [selectedValues, onChange, name, formContext],
  );

  const handleClearAll = useCallback(() => {
    onChange([]);
    try {
      if (name) formContext.setValue(name, [], { shouldValidate: true });
    } catch (err) { }
  }, [onChange, name, formContext]);

  // Handle drag end to reorder items
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = selectedValues.findIndex(
        (v) => String(v) === String(active.id)
      );
      const newIndex = selectedValues.findIndex(
        (v) => String(v) === String(over.id)
      );


      if (oldIndex === -1 || newIndex === -1) return;

      const newSelectedValues = [...selectedValues];
      const [movedItem] = newSelectedValues.splice(oldIndex, 1);
      newSelectedValues.splice(newIndex, 0, movedItem);


      onChange(newSelectedValues);
      try {
        if (name) formContext.setValue(name, newSelectedValues, { shouldValidate: true });
      } catch (err) { }
    },
    [selectedValues, onChange, name, formContext]
  );

  const closeDropdown = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const id = target?.id ?? "";

    // Don't close if clicking within the dropdown options or search input
    if (id.includes(uuid) ||
      id.includes(`multiselect-options-${uuid}`) ||
      id.includes(`multiselect-search-${uuid}`) ||
      target.closest(`[id*="multiselect-options-${uuid}"]`) ||
      target.closest(`[id*="multiselect-search-${uuid}"]`)) {
      return;
    }

    setIsOpen(false);
  }, [uuid]);

  useEffect(() => {
    if (isOpen) document.body.addEventListener("click", closeDropdown);
    else document.body.removeEventListener("click", closeDropdown);

    return () => {
      document.body.removeEventListener("click", closeDropdown);
    };
  }, [isOpen, closeDropdown]);

  const fillColor = useMemo(() => {
    if (disabled) return "#CCCCCC";
    if (hasError) return COLOR_RED;
    return "#333333";
  }, [disabled, hasError]);

  // Preserve order based on selectedValues array order
  const selectedOptions = useMemo(() => {
    return selectedValues.map(value => {
      return options.find(option =>
        option.value === value || String(option.value) === String(value)
      );
    }).filter(Boolean) as MultiSelectOptionType[];
  }, [options, selectedValues]);

  return (
    <StyledContainer
      id={`container-${uuid}`}
      $disabled={disabled}
      $width={width}
    >
      <StyledHeader>
        {label && <StyledLabel>{label}</StyledLabel>}
      </StyledHeader>

      <StyledSelect
        id={`select-container-${uuid}`}
        onClick={handleOpenSelection}
        $disabled={disabled}
        $hasError={hasError}
      >
        <StyledContent>
          {selectedOptions.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedOptions.map((opt) => String(opt.value))}
                strategy={horizontalListSortingStrategy}
              >
                <StyledSelectedItems>
                  {selectedOptions.map((option) => (
                    <SortableTag
                      key={option.id}
                      option={option}
                      onRemove={handleRemoveValue}
                    />
                  ))}
                </StyledSelectedItems>
              </SortableContext>
            </DndContext>
          ) : (
            <StyledPlaceholder>{placeholder}</StyledPlaceholder>
          )}
        </StyledContent>


        <StyledArrowIcon id={`select-dropdown-${uuid}`} $isOpen={isOpen}>
          <ArrowIcon uuid={uuid} fillColor={fillColor} />
        </StyledArrowIcon>
      </StyledSelect>

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      <MultiSelectDropdown
        isOpen={(isOpen ?? false) && !disabled}
        uuid={uuid}
        options={options}
        width={width}
        selectedValues={selectedValues}
        handleSelectOption={handleSelectOption}
        handleClearAll={handleClearAll}
        hasSearchInput={hasSearchInput}
        clearable={clearable}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $disabled?: boolean; $width: string }>`
  @media (max-width: 990px) {
    width: 100%;
  }

  width: ${({ $width }) => $width};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const StyledSelect = styled.div<{
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  width: 100%;
  min-height: 55px;
  padding: 12px 17px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  outline: none;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    min-height: 48px;
    padding: 12px 14px;
    gap: 8px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    min-height: 44px;
    padding: 12px 12px;
    gap: 6px;
    border-radius: 4px;
  }

  ${({ $disabled, $hasError }) =>
    `
      border-color: ${$disabled ? "#cccccc" : $hasError ? COLOR_RED : "#cccccc"
    };
      background-color: ${$disabled ? "#f2f2f2" : $hasError ? COLOR_LIGHT_RED : "#ffffff"
    };
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
    $hasError ? COLOR_RED : COLOR_PRIMARY};
  }
`;

const StyledContent = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const StyledSelectedItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const StyledTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: ${COLOR_PRIMARY};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
`;

const StyledTagText = styled.span`
  white-space: nowrap;
`;

const StyledRemoveButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 10px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const StyledPlaceholder = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 170%;
  letter-spacing: 0.56px;
  color: #999999;
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  transform: rotate(${({ $isOpen }) => ($isOpen ? 180 : 0)}deg);
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${COLOR_RED};
`; 